<script setup lang="ts">
import { onMounted, ref } from 'vue';
import { labCapacityApi, labsApi, type Lab, type LabCapacityEntry } from '@/services/api/emsl';
import { useNotifier } from '@/composables/notifier';

const notify = useNotifier();

type CapacityKey = 'asbestos_capacity' | 'micro_capacity' | 'lead_capacity';

const GROUPS: Array<{
  label: string;
  cap: CapacityKey;
  res: 'asbestos_reserved' | 'micro_reserved' | 'lead_reserved';
}> = [
  { label: 'Asbestos', cap: 'asbestos_capacity', res: 'asbestos_reserved' },
  { label: 'Micro',    cap: 'micro_capacity',    res: 'micro_reserved'    },
  { label: 'Lead',     cap: 'lead_capacity',     res: 'lead_reserved'     },
];

// id is null for labs that have no capacity record yet
type CapacityRow = {
  id: number | null;
  lab_id: number;
  lab: string;
  asbestos_capacity: number | null;
  asbestos_reserved: number | null;
  micro_capacity: number | null;
  micro_reserved: number | null;
  lead_capacity: number | null;
  lead_reserved: number | null;
};

const rows = ref<CapacityRow[]>([]);

const vFocus  = { mounted: (el: HTMLElement) => el.focus() };
const editing = ref<{ ri: number; col: CapacityKey } | null>(null);

function isEditing(ri: number, col: CapacityKey): boolean {
  return editing.value?.ri === ri && editing.value?.col === col;
}

function startEdit(ri: number, col: CapacityKey) {
  editing.value = { ri, col };
}

function numVal(row: CapacityRow, key: keyof CapacityRow): string {
  const v = row[key];
  return v !== null && v !== undefined ? String(v) : '';
}

async function commitEdit(e: Event, row: CapacityRow, col: CapacityKey, ri: number) {
  if (!isEditing(ri, col)) return;
  const raw      = (e.target as HTMLInputElement).value.trim();
  const newValue: number | null = raw === '' ? null : Number(raw);
  if (newValue !== null && (isNaN(newValue) || newValue < 0)) { editing.value = null; return; }
  const prev = row[col];
  editing.value = null;
  if (prev === newValue) return;
  Object.assign(row, { [col]: newValue });
  try {
    // First edit for this lab creates the record; subsequent edits update it
    const updated: LabCapacityEntry = row.id === null
      ? await labCapacityApi.create({ lab_id: row.lab_id, [col]: newValue })
      : await labCapacityApi.update({ lab_id: row.lab_id, [col]: newValue });
    Object.assign(rows.value[ri]!, updated);
  } catch {
    Object.assign(row, { [col]: prev });
    notify.error('Save failed — please try again');
  }
}

async function reload() {
  const [cap, allLabs] = await Promise.all([
    labCapacityApi.index(),
    labsApi.index() as Promise<Lab[] | { data: Lab[] }>,
  ]);
  const labList = Array.isArray(allLabs) ? allLabs : (allLabs as { data: Lab[] }).data;
  const capMap  = new Map(cap.map(c => [c.lab_id, c]));

  rows.value = labList.map(lab => capMap.get(lab.id) ?? {
    id: null,
    lab_id: lab.id,
    lab: lab.name,
    asbestos_capacity: null,
    asbestos_reserved: null,
    micro_capacity: null,
    micro_reserved: null,
    lead_capacity: null,
    lead_reserved: null,
  });
}

onMounted(reload);
</script>

<template>
  <div class="capacity">
    <h1>Lab Capacity</h1>
    <div class="grid-wrap">
      <table class="grid">
        <thead>
          <tr>
            <th class="col-lab col-lab--head" rowspan="2">Lab</th>
            <th v-for="g in GROUPS" :key="g.label" colspan="2" class="th-group">
              {{ g.label }}
            </th>
          </tr>
          <tr>
            <template v-for="g in GROUPS" :key="g.label">
              <th class="th-sub th-sub--cap">Capacity</th>
              <th class="th-sub th-sub--res">Reserved</th>
            </template>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(row, ri) in rows" :key="row.lab_id" class="row">
            <td class="col-lab col-lab--body">{{ row.lab }}</td>

            <template v-for="g in GROUPS" :key="g.label">
              <!-- Editable capacity cell -->
              <td
                class="cell cell--cap"
                :class="{ 'cell--editing': isEditing(ri, g.cap) }"
                @click="startEdit(ri, g.cap)"
              >
                <input
                  v-if="isEditing(ri, g.cap)"
                  v-focus
                  type="number"
                  min="0"
                  class="cell-input"
                  :value="numVal(row, g.cap)"
                  @blur="commitEdit($event, row, g.cap, ri)"
                  @keydown.enter.prevent="commitEdit($event, row, g.cap, ri)"
                  @keydown.escape.prevent="editing = null"
                />
                <span v-else class="cell-text">{{ numVal(row, g.cap) || '—' }}</span>
              </td>

              <!-- Read-only reserved cell -->
              <td class="cell cell--res">
                <span class="cell-text cell-text--muted">{{ numVal(row, g.res) || '0' }}</span>
              </td>
            </template>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<style scoped>
/* ── Shell ───────────────────────────────────────────────────────────── */
.capacity {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

/* ── Grid wrapper ────────────────────────────────────────────────────── */
.grid-wrap {
  overflow: auto;
  border: 1px solid #e5e7eb;
  border-radius: 0.375rem;
  max-height: calc(100vh - 120px);
}

/* ── Grid table ──────────────────────────────────────────────────────── */
.grid {
  /* Both header rows are pinned, so the second one has to know how tall the
     first is. Fixing the height here is what makes that offset knowable. */
  --head-row-h: 1.75rem;
  border-collapse: collapse;
  font-size: 0.8125rem;
  width: max-content;
  min-width: 100%;
}

/* ── Header ──────────────────────────────────────────────────────────── */
/* The header is two rows deep. Each row pins at its own offset — both at
   `top: 0` would stack them on the same line and hide the group labels. The
   z-index is equally load-bearing: the body cells are `position: relative`, so
   without one they win on DOM order and scroll straight over the header. */
.grid thead th {
  position: sticky;
  z-index: 2;
  height: var(--head-row-h);
  box-sizing: border-box;
  background: #f3f4f6;
  border-right: 1px solid #e5e7eb;
  padding: 0.35rem 0.6rem;
  font-size: 0.75rem;
  font-weight: 600;
  color: #6b7280;
  white-space: nowrap;
  text-align: center;
  user-select: none;
}

.th-group {
  top: 0;
  border-bottom: 1px solid #e5e7eb;
}

.th-sub {
  /* Overlap the group row by 1px: collapsed borders make the rendered height
     land on a fraction, and a fractional gap lets a sliver of a data row show
     through between the two pinned rows. */
  top: calc(var(--head-row-h) - 1px);
  font-weight: 500;
  font-size: 0.7rem;
  color: #9ca3af;
  border-bottom: 2px solid #d1d5db;
}

.th-sub--res {
  background: #f9fafb;
}

/* ── Sticky lab column ───────────────────────────────────────────────── */
.col-lab {
  position: sticky;
  left: 0;
  min-width: 180px;
  background: #f9fafb;
  border-right: 2px solid #d1d5db;
}

/* Stacking order, lowest first: data cells (auto) < sticky lab column (1)
   < header (2) < the lab column's own header (3), which is pinned on both
   axes and so has to sit above everything it crosses. */
/* Qualified so it outranks the `.grid thead th` z-index above. */
.grid thead th.col-lab--head {
  top: 0;
  z-index: 3;
  text-align: left;
  vertical-align: middle;
}

.col-lab--body {
  z-index: 1;
  padding: 0 0.5rem;
  font-weight: 500;
  color: #374151;
  height: 36px;
  line-height: 36px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  border-bottom: 1px solid #e5e7eb;
}

/* ── Data cells ──────────────────────────────────────────────────────── */
.cell {
  height: 36px;
  padding: 0;
  border-bottom: 1px solid #e5e7eb;
  border-right: 1px solid #f3f4f6;
  min-width: 100px;
  position: relative;
}

.cell--cap {
  cursor: cell;
}

.cell--cap:hover:not(.cell--editing) {
  background: #eff6ff;
}

.cell--res {
  cursor: default;
  background: #fafafa;
}

.cell--editing {
  outline: 2px solid #2563eb;
  outline-offset: -2px;
  background: #fff !important;
  z-index: 1;
}

/* ── Cell text ───────────────────────────────────────────────────────── */
.cell-text {
  display: block;
  padding: 0 0.6rem;
  line-height: 36px;
  color: #374151;
  text-align: right;
  white-space: nowrap;
}

.cell-text--muted {
  color: #9ca3af;
}

/* ── Cell input ──────────────────────────────────────────────────────── */
.cell-input {
  display: block;
  width: 100%;
  height: 100%;
  padding: 0 0.5rem;
  border: none;
  outline: none;
  background: transparent;
  font: inherit;
  font-size: 0.8125rem;
  color: #111827;
  text-align: right;
  box-sizing: border-box;
}
</style>
